/*
 * The shared storage must be implemented by a storage that can be shared between multiple
 * instances of shared-storage module.
 * For example, suppose both fed.moduleA and fed.moduleB modules contain reference to
 * shared-storage module. Although the instances of shared-storage are different (each
 * module has it's own instance and thay are not connected in any way), the inner storage
 * implementation must be shared between those instances.
 * In extream situations, a single module might contains several instances of shared-storage
 * (npm will add an instance for each shared-storage version). Therefore, in case the storage
 * implementation is persistent different implenetations of the storage should be backward
 * compatibility.
 * In the current implementation we use the browser memory, which is non-persistent
 * solution. Other persistent solutions can be implemented by cookies or localStorage, but
 * such solution should be wrapped by cross-domain wrapper (like an iframe).
 */

import _global from '@spotim/fed-utils/global';
import Events from '@spotim/fed-utils/events';

const storage = (_global.SPOTIM_SHARED_STORAGE = _global.SPOTIM_SHARED_STORAGE || new Map());
const vent = (_global.SPOTIM_SHARED_STORAGE_EVENTS =
  _global.SPOTIM_SHARED_STORAGE_EVENTS || new Events());

function verifyKey(key) {
  if (typeof key !== 'string') {
    throw new Error('Shared Storage only supports keys of type string');
  }
}

function getPrefix(moduleName) {
  return `${moduleName}.`;
}

function getAddress(moduleName, key) {
  var prefix = getPrefix(moduleName);
  return `${prefix}${key}`;
}

export class SharedStorage {
  constructor(moduleName) {
    this.moduleName = moduleName;
  }

  has(key, moduleName) {
    verifyKey(key);
    var address = getAddress(moduleName || this.moduleName, key);
    return storage.has(address);
  }

  get(key, moduleName) {
    verifyKey(key);
    var address = getAddress(moduleName || this.moduleName, key);
    return storage.get(address);
  }

  set(key, value) {
    verifyKey(key);
    var address = getAddress(this.moduleName, key);
    storage.set(address, value);
    vent.trigger(address, value);
  }

  remove(key) {
    verifyKey(key);
    var address = getAddress(this.moduleName, key);
    storage.delete(address);
  }

  onNextSet(key, callback) {
    verifyKey(key);
    var address = getAddress(this.moduleName, key);
    vent.once(address, callback);
  }

  getAllKeys() {
    var prefix = getPrefix(this.moduleName);
    var keys = [];
    storage.forEach((value, key) => {
      let index = key.indexOf(prefix);
      if (index === 0) {
        keys.push(key.substring(prefix.length));
      }
    });
    return keys;
  }

  getJSON() {
    return this.getAllKeys().reduce((obj, key) => {
      return { ...obj, [key]: this.get(key) };
    }, {});
  }

  clearAll() {
    var keys = this.getAllKeys();
    keys.forEach(key => this.remove(key));
  }
}
